import { FC, useCallback, useRef, useState } from 'react';
import styles from './BeforeAfterSlider.module.scss';

export interface BeforeAfterSliderPropTypes {
  beforeUrl?: string;
  afterUrl?: string;
  /** Shown full-frame when one (or both) of before/after is missing. */
  fallbackUrl: string;
  /** Caption under the frame, e.g. "Front" / "Side". */
  caption?: string;
  beforeLabel?: string;
  afterLabel?: string;
  alt?: string;
}

/**
 * Before/After image comparison slider. The "after" image sits underneath; the
 * "before" image is layered on top and clipped to `position`% width. Dragging the
 * divider handle (pointer or keyboard) wipes between them. No external dependency.
 *
 * Graceful fallback: when only one image exists it is shown full-frame without the
 * handle; when both are missing the fallback placeholder is shown.
 */
const BeforeAfterSlider: FC<BeforeAfterSliderPropTypes> = ({
  beforeUrl,
  afterUrl,
  fallbackUrl,
  caption,
  beforeLabel = 'Before',
  afterLabel = 'After',
  alt = '',
}) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);

  const hasBefore = !!beforeUrl;
  const hasAfter = !!afterUrl;
  const isComparable = hasBefore && hasAfter;

  const updateFromClientX = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    draggingRef.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    draggingRef.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') setPosition(p => Math.max(0, p - 5));
    if (e.key === 'ArrowRight') setPosition(p => Math.min(100, p + 5));
  };

  // Single-image (or empty) fallback: full-frame, no handle.
  if (!isComparable) {
    const singleUrl = beforeUrl || afterUrl || fallbackUrl;
    const singleLabel = hasBefore ? beforeLabel : hasAfter ? afterLabel : undefined;
    return (
      <div className={styles['before-after']}>
        <div className={styles['before-after__frame']}>
          <img src={singleUrl} alt={alt} className={styles['before-after__img']} />
          {singleLabel && (
            <span className={`${styles['before-after__badge']} ${styles['before-after__badge--left']}`}>
              {singleLabel}
            </span>
          )}
        </div>
        {caption && <div className={styles['before-after__caption']}>{caption}</div>}
      </div>
    );
  }

  return (
    <div className={styles['before-after']}>
      <div className={styles['before-after__frame']} ref={containerRef}>
        {/* After (bottom layer, fills frame) */}
        <img src={afterUrl} alt={alt} className={styles['before-after__img']} />
        {/* After badge in a right-anchored layer so it hides once Before covers it. */}
        <div className={styles['before-after__after']} style={{ width: `${100 - position}%` }}>
          <span className={`${styles['before-after__badge']} ${styles['before-after__badge--right']}`}>
            {afterLabel}
          </span>
        </div>

        {/* Before (top layer, clipped to position) */}
        <div className={styles['before-after__before']} style={{ width: `${position}%` }}>
          <img
            src={beforeUrl}
            alt={alt}
            className={styles['before-after__img']}
            style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100%' }}
          />
          <span className={`${styles['before-after__badge']} ${styles['before-after__badge--left']}`}>
            {beforeLabel}
          </span>
        </div>

        {/* Divider + handle */}
        <div
          className={styles['before-after__divider']}
          style={{ left: `${position}%` }}
          role="slider"
          aria-label="Before after comparison"
          aria-valuenow={Math.round(position)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onKeyDown={onKeyDown}
        >
          <span className={styles['before-after__handle']}>
            <span className={styles['before-after__handle-arrows']}>◀▶</span>
          </span>
        </div>
      </div>
      {caption && <div className={styles['before-after__caption']}>{caption}</div>}
    </div>
  );
};

BeforeAfterSlider.displayName = 'BeforeAfterSlider';
export default BeforeAfterSlider;
